import React, { useMemo, useState } from "react";
import styles from "./Checkout.module.scss";
import { useCart } from "@/hooks/useCart";
import Nav from "@/components/Header/Nav/Nav";

type OrderType = "exchpart" | "sellpart";

// ---- CRM конфіг
const CRM_ENDPOINT = "https://crm.hixa.eu/external/goodsapi/";
const CRM_TOKEN = "DH345DFGDGb32FDsf03876SDFSdf13";

// ---- Список країн
const COUNTRIES: Array<{ code: string; label: string; isDivider?: boolean }> = [
  { code: "", label: "Land wählen" },
  { code: "de", label: "Deutschland" },
  { code: "pl", label: "Polen" },
  { code: "ua", label: "Ukraine" },
  { code: "hr", label: "Kroatien" },
  { code: "al", label: "Albanien" },
  { code: "ad", label: "Andorra" },
  { code: "at", label: "Österreich" },
  { code: "by", label: "Belarus" },
  { code: "be", label: "Belgien" },
  { code: "ba", label: "Bosnien und Herzegowina" },
  { code: "bg", label: "Bulgarien" },
  { code: "cy", label: "Zypern" },
  { code: "cz", label: "Tschechien" },
  { code: "dk", label: "Dänemark" },
  { code: "ee", label: "Estland" },
  { code: "fi", label: "Finnland" },
  { code: "fr", label: "Frankreich" },
  { code: "gr", label: "Griechenland" },
  { code: "hu", label: "Ungarn" },
  { code: "is", label: "Island" },
  { code: "ie", label: "Irland" },
  { code: "it", label: "Italien" },
  { code: "xk", label: "Kosovo" },
  { code: "lv", label: "Lettland" },
  { code: "li", label: "Liechtenstein" },
  { code: "lt", label: "Litauen" },
  { code: "lu", label: "Luxemburg" },
  { code: "mt", label: "Malta" },
  { code: "md", label: "Moldau" },
  { code: "mc", label: "Monaco" },
  { code: "me", label: "Montenegro" },
  { code: "nl", label: "Niederlande" },
  { code: "mk", label: "Nordmazedonien" },
  { code: "no", label: "Norwegen" },
  { code: "pt", label: "Portugal" },
  { code: "ro", label: "Rumänien" },
  { code: "ru", label: "Russland" },
  { code: "sm", label: "San Marino" },
  { code: "rs", label: "Serbien" },
  { code: "sk", label: "Slowakei" },
  { code: "si", label: "Slowenien" },
  { code: "es", label: "Spanien" },
  { code: "se", label: "Schweden" },
  { code: "ch", label: "Schweiz" },
  { code: "tr", label: "Türkei" },
  { code: "gb", label: "Vereinigtes Königreich" },
  { code: "va", label: "Vatikanstadt" },
  { code: "n_a", label: "Keine Angabe" },
];

const Checkout: React.FC = () => {
  const { cart } = useCart(); // Отримуємо cart через хук useCart
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [postal, setPostal] = useState("");
  const [address, setAddress] = useState("");
  //--------------------------------------//
  const [receiverFirstName, setReceiverFirstName] = useState("");
  const [receiverLastName, setReceiverLastName] = useState("");
  const [receiverPhone, setReceiverPhone] = useState("");
  const [receiverEmail, setReceiverEmail] = useState("");

  const [receiverCountry, setReceiverCountry] = useState("");
  const [receiverCity, setReceiverCity] = useState("");
  const [receiverPostal, setReceiverPostal] = useState("");
  const [receiverAddress, setReceiverAddress] = useState("");
  //--------------------------------------//
  const [comment, setComment] = useState("");
  const [useDifferentAddress, setUseDifferentAddress] = useState(false);

  const [errors, setErrors] = useState<{
    country?: string;
    phone?: string;
    receiverPhone?: string;
    address?: string;
  }>({});
  const [loading, setLoading] = useState(false);

  const { clearCart } = useCart();

  const countryOptions = useMemo(
    () =>
      COUNTRIES.map((c) =>
        c.isDivider ? (
          <option key={c.code} value="" disabled>
            {c.label}
          </option>
        ) : (
          <option key={c.code} value={c.code}>
            {c.label}
          </option>
        )
      ),
    []
  );

  // Валідація для номера телефону
  function validatePhone(number: string) {
    const trimmed = number.trim();
    return /^[0-9]{8,14}$/.test(trimmed); // Дозволяє тільки цифри від 8 до 14 символів
  }

  // Функція для перевірки, чи є невірні символи в номері телефону
  function containsInvalidChar(number: string) {
    return /[^0-9]/.test(number); // Якщо є нецифрові символи
  }

  // Обробка зміни телефону платника та валідація в реальному часі
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhone(value);

    // Якщо ввели невірний символ, підсвітити поле
    if (containsInvalidChar(value)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        phone: "Bitte nur Ziffern eingeben", // Помилка для номера телефону платника
      }));
    } else {
      // Якщо немає невірних символів, перевіряємо довжину
      setErrors((prevErrors) => {
        const { phone, ...rest } = prevErrors; // Видаляємо помилку для телефону платника
        return rest;
      });
    }
  };

  // Обробка зміни телефону отримувача та валідація в реальному часі
  const handleReceiverPhoneChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setReceiverPhone(value);

    // Якщо ввели невірний символ, підсвітити поле
    if (containsInvalidChar(value)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        receiverPhone: "Bitte nur Ziffern eingeben", // Помилка для телефону отримувача
      }));
    } else {
      // Якщо немає невірних символів, перевіряємо довжину
      setErrors((prevErrors) => {
        const { receiverPhone, ...rest } = prevErrors; // Видаляємо помилку для телефону отримувача
        return rest;
      });
    }
  };

  // Функція для обробки втрати фокусу (onBlur), щоб перевірити довжину номера
  const handleBlurPhone = () => {
    if (phone.trim() && !validatePhone(phone)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        phone: "Ungültige Telefonnummer", // Помилка для номера телефону платника
      }));
    }
    if (receiverPhone.trim() && !validatePhone(receiverPhone)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        receiverPhone: "Ungültige Telefonnummer des Empfängers", // Помилка для телефону отримувача
      }));
    }
  };

  // ---- Функція для створення масиву одиничних товарів
  function createIndividualItems() {
    const individualItems: Array<{
      goid: string;
      quantity: number;
      typeorder: OrderType;
    }> = [];

    cart.forEach((item) => {
      // Для кожного товару створюємо quantity окремих позицій по 1 штуці
      for (let i = 0; i < item.quantity; i++) {
        individualItems.push({
          goid: String(item.goid),
          quantity: 1,
          typeorder: item.typeorder,
        });
      }
    });

    return individualItems;
  }

  // ---- Формуємо payload з одиничними товарами
  function buildOrderPayload() {
    const goods = createIndividualItems();

    // Контакти для платника
    const contacts = [
      phone.trim()
        ? { type: "mobilephone", value: phone.trim().replace(/\s+/g, "") }
        : null,
      email.trim() ? { type: "email", value: email.trim() } : null,
    ].filter(Boolean) as Array<{ type: string; value: string }>;

    // Контакти для отримувача
    const lcontacts = [
      receiverPhone.trim()
        ? {
            type: "lmobilephone",
            value: receiverPhone.trim().replace(/\s+/g, ""),
          }
        : null,
      receiverEmail.trim()
        ? { type: "lemail", value: receiverEmail.trim() }
        : null,
    ].filter(Boolean) as Array<{ type: string; value: string }>;

    return {
      type: "orderadd",
      success_url: "https://hixa.eu/payment_success",
      cancel_url: "https://hixa.eu/payment_failed",
      goods,
      description: (comment || "").trim() || "-",
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      contacts,
      country: country,
      index: postal.trim(),
      city: city.trim(),
      address: address.trim(),
      receiver: useDifferentAddress
        ? {
            lfirstName: receiverFirstName.trim(),
            llastName: receiverLastName.trim(),
            lcontacts,
            laddress: receiverAddress.trim(),
            lcity: receiverCity.trim(),
            lpostal: receiverPostal.trim(),
            lcountry: receiverCountry.trim(),
          }
        : null,
    };
  }

  // ---- Перекодуємо payload у x-www-form-urlencoded
  function toFormData(p: ReturnType<typeof buildOrderPayload>) {
    const params = new URLSearchParams();
    params.set("type", p.type);

    // Додаємо товари
    p.goods.forEach((g, i) => {
      params.set(`goods[${i}][goid]`, String(g.goid));
      params.set(`goods[${i}][quantity]`, String(g.quantity));
      params.set(`goods[${i}][typeorder]`, g.typeorder);
    });

    // Додаємо основні дані
    params.set("description", p.description);
    params.set("first_name", p.first_name);
    params.set("last_name", p.last_name);

    // Додаємо контакти платника
    p.contacts.forEach((c, i) => {
      params.set(`contacts[${i}][type]`, c.type);
      params.set(`contacts[${i}][value]`, c.value);
    });

    // Додаємо основну адресу
    params.set("country", p.country);
    params.set("index", p.index);
    params.set("city", p.city);
    params.set("address", p.address);

    // Якщо є отримувач, додаємо його дані
    if (p.receiver) {
      params.set("receiver[first_name]", p.receiver.lfirstName);
      params.set("receiver[last_name]", p.receiver.llastName);

      // Додаємо контакти отримувача
      p.receiver.lcontacts.forEach((c, i) => {
        params.set(`receiver[lcontacts][${i}][type]`, c.type);
        params.set(`receiver[lcontacts][${i}][value]`, c.value);
      });

      // Додаємо адресу отримувача
      params.set("receiver[address]", p.receiver.laddress);
      params.set("receiver[city]", p.receiver.lcity);
      params.set("receiver[postal]", p.receiver.lpostal);
      params.set("receiver[country]", p.receiver.lcountry);
    }

    return params;
  }

  const LEGID = "1";

  async function sendToCrmFormUrlencoded(params: URLSearchParams) {
    const body = new URLSearchParams(params);
    body.set("legid", LEGID);

    // Лог перед відправкою запиту
    console.log("Відправляємо запит на сервер з параметрами:", body.toString());

    try {
      const res = await fetch(CRM_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${CRM_TOKEN}`,
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          Accept: "application/json",
        },
        body: body.toString(),
      });

      // Лог після запиту
      console.log("Відповідь від сервера:", res);

      return res;
    } catch (error) {
      // Лог на випадок помилки fetch
      console.error("Помилка при виконанні запиту:", error);
    }
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Отримуємо кнопку, яка була натиснута, типізуємо її як HTMLButtonElement
    const button = (e.nativeEvent as SubmitEvent)
      .submitter as HTMLButtonElement;

    if (button?.name === "bestellung") {
      // Обробка для кнопки Bestellung senden
      handleSubmit(e);
    } else if (button?.name === "feedback") {
      // Обробка для кнопки Feedback senden
      handleFeedbackSubmit(e);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Лог перед валідацією
    console.log("Дані форми перед відправкою:", {
      firstName,
      lastName,
      phone,
      email,
      country,
      city,
      postal,
      address,
      receiverFirstName,
      receiverLastName,
      receiverPhone,
      receiverEmail,
      receiverCountry,
      receiverCity,
      receiverPostal,
      receiverAddress,
    });

    const newErrors: typeof errors = {};
    if (!country) newErrors.country = "Bitte Land auswählen.";
    if (!validatePhone(phone)) newErrors.phone = "Ungültige Telefonnummer."; // Валідація телефону
    if (!address) newErrors.address = "Adresse ist erforderlich.";
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    if (!cart || cart.length === 0) {
      alert("Der Warenkorb ist leer.");
      return;
    }

    const payload = buildOrderPayload();
    const formParams = toFormData(payload);

    // Лог payload перед відправкою
    console.log("Запит перед відправкою:", formParams);

    try {
      const res = await sendToCrmFormUrlencoded(formParams);

      // Перевірка, чи res існує
      if (!res) {
        console.error("❌ Запит не відправлений: res undefined");
        alert("❌ Senden fehlgeschlagen. Bitte später erneut versuchen.");
        return;
      }

      let bodyText = "";
      try {
        const data = await res.clone().json();
        bodyText = JSON.stringify(data, null, 2);
        console.log("Відповідь від сервера:", data); // Лог відповіді сервера

        // Перевірка на success
        if (data.success === 1) {
          // Якщо success = 1, редіректимо користувача на сторінку для оплати (stripelink)
          const stripeLink = data.stripelink;
          if (stripeLink) {
            console.log("Redirecting to Stripe:", stripeLink); // Лог для дебагу
            window.location.href = stripeLink; // Перенаправляємо на сторінку Stripe
          }
        } else if (data.success === 0) {
          // Якщо success = 0, виводимо помилку
          const errorMessage = data.error || "Error.";
          console.error("❌ Error from server:", errorMessage);
          alert(errorMessage); // Показуємо помилку
        }
      } catch (err) {
        bodyText = await res.text();
        console.error("❌ Error parsing server response:", err);
      }

      // Обробка статусу відповіді
      if (res.ok) {
        window.dataLayer.push({
          event: "order_submit",
          form_status: "success",
          form_name: "order_form",
          total_items: payload.goods.length,
          unique_products: cart.length,
        });

        localStorage.removeItem("cart");
        if (clearCart) clearCart();
        setFirstName("");
        setLastName("");
        setPhone("");
        setEmail("");
        setComment("");
        setCountry("");
        setCity("");
        setPostal("");
        setAddress("");
        setReceiverFirstName("");
        setReceiverLastName("");
        setReceiverPhone("");
        setReceiverEmail("");
        setReceiverAddress("");
        setReceiverCity("");
        setReceiverPostal("");
        setReceiverCountry("");
        setUseDifferentAddress(false);

        alert(
          `✅ ${payload.goods.length} Bestellung(en) wurden erfolgreich an das CRM gesendet.`
        );
      } else {
        console.error("❌ CRM-Fehler", {
          httpStatus: res.status,
          responseBody: bodyText,
        });
        alert("❌ Senden fehlgeschlagen. Bitte später erneut versuchen.");
      }
    } catch (err: any) {
      console.error("❌ Technischer Fehler beim Senden:", err);
      alert("❌ Technischer Fehler. Bitte später erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  // ---- Функція для обробки відправки форми з feedback: 1
  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Лог перед валідацією
    console.log("Дані форми перед відправкою для feedback:", {
      firstName,
      lastName,
      phone,
      email,
      country,
      city,
      postal,
      address,
      receiverFirstName,
      receiverLastName,
      receiverPhone,
      receiverEmail,
      receiverCountry,
      receiverCity,
      receiverPostal,
      receiverAddress,
    });

    const newErrors: typeof errors = {};
    if (!country) newErrors.country = "Bitte Land auswählen.";
    if (!validatePhone(phone)) newErrors.phone = "Ungültige Telefonnummer."; // Валідація телефону
    if (!address) newErrors.address = "Adresse ist erforderlich.";
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    if (!cart || cart.length === 0) {
      alert("Der Warenkorb ist leer.");
      return;
    }

    const payload = buildOrderPayload();
    const formParams = toFormData(payload);

    // Додаємо feedback: 1 до запиту
    formParams.set("feedback", "1");

    // Лог payload перед відправкою
    console.log("Запит перед відправкою для feedback:", formParams);

    try {
      const res = await sendToCrmFormUrlencoded(formParams);

      // Перевірка, чи res існує
      if (!res) {
        console.error("❌ Запит не відправлений: res undefined");
        alert("❌ Senden fehlgeschlagen. Bitte später erneut versuchen.");
        return;
      }

      let bodyText = "";
      try {
        const data = await res.clone().json();
        bodyText = JSON.stringify(data, null, 2);
        console.log("Відповідь від сервера:", data); // Лог відповіді сервера

        // Перевірка на success
        if (data.success === 1) {
          // Якщо success = 1, перенаправляємо на сторінку feedback_success
          console.log("Redirecting to feedback success page");
          window.location.replace("/feedback_success"); // Перенаправляємо на сторінку feedback_success без використання повного URL
        } else if (data.success === 0) {
          // Якщо success = 0, виводимо помилку
          const errorMessage = data.error || "Error.";
          console.error("❌ Error from server:", errorMessage);
          alert(errorMessage); // Показуємо помилку
        }
      } catch (err) {
        bodyText = await res.text();
        console.error("❌ Error parsing server response:", err);
      }

      // Обробка статусу відповіді
      if (res.ok) {
        // Лог успішної відправки
        window.dataLayer.push({
          event: "order_submit",
          form_status: "success",
          form_name: "feedback_form",
          total_items: payload.goods.length,
          unique_products: cart.length,
        });

        // Очищаємо форму після відправки
        setFirstName("");
        setLastName("");
        setPhone("");
        setEmail("");
        setComment("");
        setCountry("");
        setCity("");
        setPostal("");
        setAddress("");
        setReceiverFirstName("");
        setReceiverLastName("");
        setReceiverPhone("");
        setReceiverEmail("");
        setReceiverAddress("");
        setReceiverCity("");
        setReceiverPostal("");
        setReceiverCountry("");
        setUseDifferentAddress(false);

        alert("✅ Ваш відгук був успішно надісланий!");
      } else {
        console.error("❌ CRM-Fehler", {
          httpStatus: res.status,
          responseBody: bodyText,
        });
        alert("❌ Senden fehlgeschlagen. Bitte später erneut versuchen.");
      }
    } catch (err: any) {
      console.error("❌ Technischer Fehler beim Senden:", err);
      alert("❌ Technischer Fehler. Bitte später erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Nav />
      <div className={`pageSection ${styles.pageSection}`}>
        <div className={styles.form} onClick={(e) => e.stopPropagation()}>
          <img
            className={styles.stripeLogo}
            src="/images/stripe.png"
            alt="stripe"
          />
          <form onSubmit={handleFormSubmit}>
            <div className={styles.formRow}>
              <select
                className={styles.select}
                value={country}
                required
                onChange={(e) => setCountry(e.target.value)}
              >
                {countryOptions}
              </select>
            </div>

            <div className={styles.formRow}>
              <input
                className={styles.w50}
                type="text"
                placeholder="Vorname*"
                value={firstName}
                required
                autoComplete="given-name"
                onChange={(e) => setFirstName(e.target.value)}
              />
              <input
                className={styles.w50}
                type="text"
                placeholder="Nachname*"
                value={lastName}
                required
                autoComplete="family-name"
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>

            <div className={styles.formRow}>
              <input
                type="text"
                placeholder="Adresse*"
                value={address}
                required
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            <div className={styles.formRow}>
              <input
                type="email"
                placeholder="E-Mail*"
                value={email}
                required
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className={styles.formRow}>
              <input
                type="text"
                placeholder="Stadt*"
                value={city}
                required
                autoComplete="address-level2"
                onChange={(e) => setCity(e.target.value)}
              />
              <input
                type="text"
                placeholder="PLZ*"
                value={postal}
                required
                autoComplete="postal-code"
                onChange={(e) => setPostal(e.target.value)}
              />
            </div>

            <div className={styles.formRow}>
              <input
                type="tel"
                placeholder="Telefonnummer*"
                value={phone}
                required
                inputMode="tel"
                onChange={handlePhoneChange} // Використовуємо нову функцію для обробки телефону
                onBlur={handleBlurPhone} // Викликаємо валідацію при втраті фокусу
                style={{
                  backgroundColor: errors.phone ? "#FFB3B3" : "", // Підсвітка поля пастельним червоним
                  outline: errors.phone ? "1px solid red" : "", // Додаємо ще один обвід
                }}
              />
            </div>

            <div className={styles.formRow}>
              <textarea
                placeholder="Kommentar"
                value={comment}
                rows={4}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>

            {/* Чекбокс для адреси отримувача */}
            <div className={styles.formRow}>
              <label className={styles.infoLabel}>
                <input
                  type="checkbox"
                  checked={useDifferentAddress}
                  onChange={() => setUseDifferentAddress(!useDifferentAddress)}
                />
                Die Adresse des Empfängers unterscheidet sich von der Adresse
                des Zahlers
              </label>
            </div>

            {/* Поля для адреси отримувача */}
            {useDifferentAddress && (
              <>
                <div className={styles.formRow}>
                  <select
                    className={styles.select}
                    value={receiverCountry}
                    required
                    onChange={(e) => setReceiverCountry(e.target.value)}
                  >
                    {countryOptions}
                  </select>
                </div>

                <div className={styles.formRow}>
                  <input
                    type="text"
                    placeholder="Vorname des Empfängers*"
                    value={receiverFirstName}
                    required
                    onChange={(e) => setReceiverFirstName(e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Nachname des Empfängers*"
                    value={receiverLastName}
                    required
                    onChange={(e) => setReceiverLastName(e.target.value)}
                  />
                </div>
                <div className={styles.formRow}>
                  <input
                    type="text"
                    placeholder="Adresse des Empfängers*"
                    value={receiverAddress}
                    required
                    onChange={(e) => setReceiverAddress(e.target.value)}
                  />
                </div>

                <div className={styles.formRow}>
                  <input
                    type="email"
                    placeholder="E-Mail des Empfängers*"
                    value={receiverEmail}
                    required
                    autoComplete="email"
                    onChange={(e) => setReceiverEmail(e.target.value)}
                  />
                </div>

                <div className={styles.formRow}>
                  <input
                    type="text"
                    placeholder="Stadt des Empfängers*"
                    value={receiverCity}
                    required
                    onChange={(e) => setReceiverCity(e.target.value)}
                  />

                  <input
                    type="text"
                    placeholder="PLZ des Empfängers*"
                    value={receiverPostal}
                    required
                    onChange={(e) => setReceiverPostal(e.target.value)}
                  />
                </div>

                <div className={styles.formRow}>
                  <input
                    type="tel"
                    placeholder="Telefonnummer des Empfängers*"
                    value={receiverPhone}
                    required
                    onChange={handleReceiverPhoneChange} // Використовуємо нову функцію для обробки телефону отримувача
                    onBlur={handleBlurPhone} // Викликаємо валідацію при втраті фокусу
                    style={{
                      backgroundColor: errors.receiverPhone ? "#FFB3B3" : "", // Підсвітка поля пастельним червоним для отримувача
                      outline: errors.receiverPhone ? "1px solid red" : "", // Додаємо ще один обвід
                    }}
                  />
                </div>
              </>
            )}

            <div className={styles.buttonBlock}>
              <button
                className={`commonButton buttonMd ${styles.button} ${styles.payButton}`}
                type="submit"
                name="bestellung" // Атрибут для ідентифікації кнопки
                disabled={loading}
              >
                {loading ? "Senden…" : "Jetzt kaufen & bezahlen"}
              </button>
              <p className={styles.infoLabel}>
                Mit dem Klick auf „Jetzt kaufen & bezahlen“ schließen Sie den
                Kaufvertrag ab.
              </p>
              <button
                className={`commonButton buttonMd ${styles.button} ${styles.feedbackButton}`}
                type="submit"
                name="feedback" // Атрибут для ідентифікації кнопки
                disabled={loading}
              >
                {loading ? "Senden…" : "Anfrage senden – Rückruf gewünscht"}
              </button>
            </div>
          </form>
        </div>
        <div className={styles.cartBlock}>
          {cart.length === 0 ? (
            <p>Your cart is empty.</p>
          ) : (
            <ul>
              {cart.map((item) => (
                <li
                  key={`${item.goid}-${item.typeorder}`}
                  className={styles.cartItem}
                >
                  <div className={styles.imgBlock}>
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className={styles.cartItemImage}
                    />
                    <p className={styles.quantity}>{item.quantity}</p>
                  </div>
                  <h4>{item.name}</h4>
                  <p className={styles.price}>€{item.price * item.quantity}</p>
                  <div className={styles.cartItemActions}>
                    {/* Додати кнопки для збільшення/зменшення кількості або видалення товару */}
                  </div>
                </li>
              ))}
              <li className={styles.summary}>
                <p>
                  Zwischensumme ·{" "}
                  {cart.reduce((acc, item) => acc + item.quantity, 0)} Artikel ·
                </p>
                <p>
                  {cart
                    .reduce((acc, item) => acc + item.price * item.quantity, 0)
                    .toFixed(2)}
                  €
                </p>
              </li>
              <li className={styles.summary}>
                <p className={styles.total}>Gesamt</p>
                <p className={styles.totalPriceBlock}>
                  <p className={styles.fs12}>EUR </p>
                  <p className={styles.totalPrice}>
                    {cart
                      .reduce(
                        (acc, item) => acc + item.price * item.quantity,
                        0
                      )
                      .toFixed(2)}
                    €
                  </p>
                </p>
              </li>
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default Checkout;
